import React, { useEffect, useRef, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import { useMessage } from 'studio/components/Message';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import services from 'services';
import { MonacoDiffEditor, MonacoEditor } from 'components/monaco';
import Alert from 'studio/components/Alert';
import SendIcon from '@mui/icons-material/SendRounded';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Progress from 'studio/components/Progress';
import TextArea from 'studio/components/TextArea';
import { trackEvent } from 'utils/eventTracking';
import Tooltip from '@mui/material/Tooltip';
import { withTheme } from '@mui/styles';

const useStyles = makeStyles(theme => ({
  root: {},
  questionTextArea: {
    marginTop: 0,
    marginBottom: theme.spacing(3)
  },
  editorHeaders: {
    display: 'flex',
    marginBottom: theme.spacing(2),
    '& .MuiTypography-h3': {
      marginTop: 0
    }
  },
  editedVklEditorHeader: {
    marginLeft: -35
  },
  editorContainer: {
    overflow: 'hidden'
  }
}));

let editor = null;

const CopilotEditVklDialog = ({ theme, isOpen, knowledgeGraph, vkl, file, isSelection, setVkl, onClose }) => {
  const { showConfirmationModal } = useConfirmationModal();
  const { errorMessage } = useMessage();
  const classes = useStyles();

  // the current user prompt in the text area
  const [userPrompt, setUserPrompt] = useState('');
  // the history of user prompts, from oldest to newest
  const [userPrompts, setUserPrompts] = useState([]);
  // true after a request has been issued until we get the response
  const [isLoading, setIsLoading] = useState(false);
  // the edited VKL returned from the backend
  const [editedVkl, setEditedVkl] = useState(null);
  // specifies whether the edited VKL has been truncated
  const [editedVklIsTruncated, setEditedVklIsTruncated] = useState(false);
  // the height of the editor container
  const [editorContainerHeight, setEditorContainerHeight] = useState(null);

  // specifies whether a user prompt can be submitted
  const canSubmitUserPrompt = !!userPrompt.trim() && !isLoading;

  const questionTextAreaInputRef = useRef(null);
  const originalVklEditorHeaderRef = useRef(null);
  const editorContainerRef = useRef(null);

  // resets the state
  const reset = keepUserPrompt => {
    if (!keepUserPrompt) {
      setUserPrompt('');
    }
    setUserPrompts([]);
    setIsLoading(false);
    setEditedVkl(null);
    setEditedVklIsTruncated(false);
    setEditorContainerHeight(null);
    questionTextAreaInputRef.current?.select();
  };

  // resets the state when the dialog is opened
  useEffect(() => {
    if (isOpen) {
      trackEvent('Studio Copilot Edit VKL Opened', { file, isSelection });
      reset();
    }
  }, [isOpen]);

  // requests the backend to edit the VKL
  const editVkl = async () => {
    const newUserPrompts = [...userPrompts, userPrompt];
    trackEvent('Studio Copilot Edit VKL Asked', { file, isSelection, userPrompts: newUserPrompts });
    setIsLoading(true);
    const response = await services.editVkl(knowledgeGraph.data.id, editedVkl || vkl, newUserPrompts, file);
    setIsLoading(false);
    if (response.success) {
      setUserPrompts(newUserPrompts);
      setEditedVkl(response.data.editedVkl);
      setEditedVklIsTruncated(response.data.editedVklIsTruncated);
    } else {
      errorMessage('Studio Copilot encountered an error, please try again');
    }
    // select the user prompt s.t. one can continue to type without clicking
    questionTextAreaInputRef.current?.select();
  };

  // is called when the editor or diff editor has been mounted
  const editorDidMount = newEditor => {
    editor = newEditor;

    // collect the heights (such that we can use the same function for the normal and diff editor)
    const heights = [
      editor.getContentHeight?.() || 0,
      editor.getOriginalEditor?.().getContentHeight() || 0,
      editor.getModifiedEditor?.().getContentHeight() || 0
    ];
    // the minimum height for the editor(s)
    const minHeight = 250;
    // the maximum height for the editor(s)
    const maxHeight = Math.max(500, window.innerHeight - 500);
    const height = Math.min(maxHeight, Math.max(minHeight, ...heights));
    setEditorContainerHeight(height);

    // for the diff editor...
    if (editor.getOriginalEditor?.()) {
      // ...update the width of the "Original VKL" editor header
      updateOriginalVklEditorHeaderWidth();
      // ...and registr listener to updated it whenever the split view is resized
      editor.getOriginalEditor().onDidLayoutChange(updateOriginalVklEditorHeaderWidth);
    }
  };

  // updates the width of the "Original VKL" editor header in order to correctly position the "Edited VKL" one
  const updateOriginalVklEditorHeaderWidth = () => {
    const modifiedEditor = document.getElementsByClassName('modified-in-monaco-diff-editor')?.[0];
    const originalVklEditorHeader = originalVklEditorHeaderRef.current;
    if (modifiedEditor && originalVklEditorHeader) {
      const width = modifiedEditor.getBoundingClientRect().x - originalVklEditorHeader.getBoundingClientRect().x;
      originalVklEditorHeaderRef.current.style.width = `${width}px`;
    }
  };

  // sets the editor container's height and instructs the editor to layout
  useEffect(() => {
    if (editorContainerRef.current) {
      editorContainerRef.current.style.height = `${editorContainerHeight}px`;
      editor.layout();
    }
  }, [editorContainerRef, editorContainerHeight]);

  const cancel = () => {
    if (editedVkl) {
      showConfirmationModal(() => ({
        title: 'Discard edited VKL',
        content: 'Do you really want to discard the edited VKL?',
        confirmButtonText: 'Discard',
        onConfirm: onClose
      }));
    } else {
      onClose();
    }
  };

  const apply = () => {
    trackEvent('Studio Copilot Edit VKL Applied', { file, isSelection, userPrompts });
    setVkl(editedVkl);
    onClose();
  };

  const editorOptions = {
    scrollBeyondLastLine: false,
    showUnused: false,
    codeLens: false,
    automaticLayout: true,
    scrollbar: { verticalScrollbarSize: 6 },
    glyphMargin: false,
    lineNumbersMinChars: 3,
    originalEditable: false,
    minimap: { enabled: false }
  };

  return (
    <Dialog
      open={isOpen}
      className={classes.root}
      maxWidth="xl"
      fullWidth
      onClose={cancel}
      data-test="copilotEditVklDialog"
    >
      <DialogTitle>Studio Copilot</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Ask Studio Copilot to edit the {isSelection ? 'selected VKL' : 'VKL file'} to your liking.
        </Typography>
        <TextArea
          ref={questionTextAreaInputRef}
          className={classes.questionTextArea}
          placeholder="How do you want the VKL to change?"
          onChange={event => setUserPrompt(event.target.value)}
          onKeyPress={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              if (canSubmitUserPrompt) {
                editVkl();
              }
            }
          }}
          size="medium"
          minRows={1}
          autoFocus
          value={userPrompt}
          disabled={isLoading}
          endIcon={
            isLoading ? (
              <Progress size={30} />
            ) : (
              <IconButton size="large" onClick={editVkl} disabled={!canSubmitUserPrompt}>
                <SendIcon color={canSubmitUserPrompt ? 'primary' : undefined} />
              </IconButton>
            )
          }
        />
        {editedVklIsTruncated && (
          <Alert severity="warning" className={classes.alert}>
            The edited VKL has been truncated because it is too long. Applying the changes will remove parts of the
            original VKL as indicated in the diff.
          </Alert>
        )}

        <div className={classes.editorHeaders}>
          <Typography variant="h3" ref={originalVklEditorHeaderRef}>
            Original VKL
          </Typography>
          {!!editedVkl && <Typography variant="h3">Edited VKL</Typography>}
        </div>

        <div className={classes.editorContainer} ref={editorContainerRef}>
          {vkl && !editedVkl && (
            <MonacoEditor
              editorDidMount={editorDidMount}
              language="vkl"
              theme={theme.palette.mode}
              options={{
                ...editorOptions,
                readOnly: true
              }}
              value={vkl}
            />
          )}
          {vkl && editedVkl && (
            <MonacoDiffEditor
              editorDidMount={editorDidMount}
              language="vkl"
              theme={theme.palette.mode}
              options={editorOptions}
              onChange={setEditedVkl}
              original={vkl}
              value={editedVkl}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={cancel}>
          Cancel
        </Button>
        <Tooltip title="Reset the changes from Copilot" placement="top" arrow>
          <Button color="primary" onClick={() => reset(true)} disabled={isLoading || !editedVkl || editedVkl === vkl}>
            Reset
          </Button>
        </Tooltip>
        <Tooltip title="Apply the changes from Copilot to the Editor" placement="top" arrow>
          <Button
            color="primary"
            variant="contained"
            onClick={apply}
            disabled={isLoading || !editedVkl || editedVkl === vkl}
          >
            Apply
          </Button>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};

export default withTheme(CopilotEditVklDialog);
